<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-tabs v-model="activeTab" background-color="grey darken-3" dark>
            <v-tab>Jetson Devices</v-tab>
            <v-tab>Model Architectures</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- Devices Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-title>
                  <span class="text-h5">Jetson Devices</span>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="deviceSearch"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line hide-details dense outlined
                    class="mr-4" style="max-width: 300px"
                  ></v-text-field>
                  <v-btn color="success" @click="seedDevices" :loading="seedingDevices" class="mr-2">
                    <v-icon left>mdi-seed</v-icon> Seed Defaults
                  </v-btn>
                  <v-btn color="primary" outlined @click="importDialog = true" class="mr-2">
                    <v-icon left>mdi-code-json</v-icon> Import JSON
                  </v-btn>
                  <v-btn color="primary" @click="openDeviceDialog()">
                    <v-icon left>mdi-plus</v-icon> Add Device
                  </v-btn>
                  <v-btn icon @click="fetchDevices" class="ml-2">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="deviceHeaders"
                    :items="devices"
                    :search="deviceSearch"
                    :loading="devicesLoading"
                    :items-per-page="15"
                    class="elevation-1"
                  >
                    <template v-slot:[`item.unified_memory_gb`]="{ item }">
                      {{ item.unified_memory_gb }} GB
                    </template>
                    <template v-slot:[`item.memory_bandwidth_gbs`]="{ item }">
                      {{ item.memory_bandwidth_gbs }} GB/s
                    </template>
                    <template v-slot:[`item.image_url`]="{ item }">
                      <v-avatar v-if="item.image_url" size="36" tile class="rounded">
                        <v-img :src="item.image_url" contain></v-img>
                      </v-avatar>
                      <v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
                    </template>
                    <template v-slot:[`item.product_url`]="{ item }">
                      <a v-if="item.product_url" :href="item.product_url" target="_blank" rel="noopener" :title="item.product_url">
                        <v-icon small color="primary">mdi-open-in-new</v-icon>
                      </a>
                      <v-chip v-else x-small outlined color="grey">No link</v-chip>
                    </template>
                    <template v-slot:[`item.preliminary`]="{ item }">
                      <v-chip v-if="item.preliminary" x-small color="amber" outlined>Yes</v-chip>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="openDeviceDialog(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="confirmDeleteDevice(item)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Model Arch Tab -->
            <v-tab-item>
              <v-card flat>
                <v-card-title>
                  <span class="text-h5">Model Architectures</span>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="archSearch"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line hide-details dense outlined
                    class="mr-4" style="max-width: 300px"
                  ></v-text-field>
                  <v-btn color="success" @click="seedArchs" :loading="seedingArchs" class="mr-2">
                    <v-icon left>mdi-seed</v-icon> Seed Defaults
                  </v-btn>
                  <v-btn color="primary" @click="openArchDialog()">
                    <v-icon left>mdi-plus</v-icon> Add Architecture
                  </v-btn>
                  <v-btn icon @click="fetchArchs" class="ml-2">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="archHeaders"
                    :items="archs"
                    :search="archSearch"
                    :loading="archsLoading"
                    :items-per-page="15"
                    class="elevation-1"
                  >
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="openArchDialog(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="confirmDeleteArch(item)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>

    <!-- Device Add/Edit Dialog -->
    <jetson-device-dialog
      v-model="deviceDialog"
      :device="editingDevice"
      @saved="onDeviceSaved"
      @error="showSnack($event, 'error')"
    ></jetson-device-dialog>
    <jetson-import-dialog
      v-model="importDialog"
      @saved="onDeviceSaved"
      @error="showSnack($event, 'error')"
    ></jetson-import-dialog>

    <!-- Arch Add/Edit Dialog -->
    <jetson-arch-dialog
      v-model="archDialog"
      :arch="editingArch"
      @saved="onArchSaved"
      @error="showSnack($event, 'error')"
    ></jetson-arch-dialog>

    <!-- Delete Confirmation -->
    <jetson-delete-dialog
      v-model="deleteDialog"
      :target="deleteTarget"
      :type="deleteType"
      @deleted="onDeleted"
      @error="showSnack($event, 'error')"
    ></jetson-delete-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">{{ snackbarText }}</v-snackbar>
  </v-container>
</template>

<script>
import { apiService } from '../service/api.service'
import JetsonDeviceDialog from '../components/models/JetsonDeviceDialog.vue'
import JetsonImportDialog from '../components/models/JetsonImportDialog.vue'
import JetsonArchDialog from '../components/models/JetsonArchDialog.vue'
import JetsonDeleteDialog from '../components/models/JetsonDeleteDialog.vue'

export default {
  name: 'JetsonCompatibility',
  components: { JetsonDeviceDialog, JetsonImportDialog, JetsonArchDialog, JetsonDeleteDialog },
  data () {
    return {
      activeTab: 0,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Devices
      devices: [],
      devicesLoading: false,
      deviceSearch: '',
      deviceDialog: false,
      importDialog: false,
      editingDevice: null,
      seedingDevices: false,
      deviceHeaders: [
        { text: '', value: 'image_url', sortable: false, width: 56 },
        { text: 'Name', value: 'name' },
        { text: 'Series', value: 'series' },
        { text: 'Memory', value: 'unified_memory_gb' },
        { text: 'TOPS', value: 'tops' },
        { text: 'GPU Arch', value: 'gpu_arch' },
        { text: 'Bandwidth', value: 'memory_bandwidth_gbs' },
        { text: 'Product page', value: 'product_url', sortable: false },
        { text: 'Preliminary', value: 'preliminary' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      // Model Archs
      archs: [],
      archsLoading: false,
      archSearch: '',
      archDialog: false,
      editingArch: null,
      seedingArchs: false,
      archHeaders: [
        { text: 'Family', value: 'family' },
        { text: 'Layers', value: 'n_layers' },
        { text: 'KV Heads', value: 'n_kv_heads' },
        { text: 'Head Dim', value: 'head_dim' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      // Delete
      deleteDialog: false,
      deleteTarget: null,
      deleteType: ''
    }
  },
  mounted () {
    this.fetchDevices()
    this.fetchArchs()
  },
  methods: {
    showSnack (text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
    // --- Devices ---
    async fetchDevices () {
      this.devicesLoading = true
      try {
        const { data } = await apiService.get('admin/llm_models/jetson-devices')
        this.devices = data.data || []
      } catch (e) { this.showSnack('Failed to load devices', 'error') }
      this.devicesLoading = false
    },
    openDeviceDialog (item = null) {
      this.editingDevice = item || null
      this.deviceDialog = true
    },
    onDeviceSaved (message) {
      this.showSnack(message)
      this.fetchDevices()
    },
    confirmDeleteDevice (item) {
      this.deleteTarget = item
      this.deleteType = 'device'
      this.deleteDialog = true
    },
    async seedDevices () {
      this.seedingDevices = true
      try {
        const { data } = await apiService.post('admin/llm_models/jetson-devices/seed-defaults')
        this.showSnack(data.message)
        this.fetchDevices()
      } catch (e) { this.showSnack('Seed failed', 'error') }
      this.seedingDevices = false
    },
    // --- Archs ---
    async fetchArchs () {
      this.archsLoading = true
      try {
        const { data } = await apiService.get('admin/llm_models/jetson-model-arch')
        this.archs = data.data || []
      } catch (e) { this.showSnack('Failed to load architectures', 'error') }
      this.archsLoading = false
    },
    openArchDialog (item = null) {
      this.editingArch = item || null
      this.archDialog = true
    },
    onArchSaved (message) {
      this.showSnack(message)
      this.fetchArchs()
    },
    confirmDeleteArch (item) {
      this.deleteTarget = item
      this.deleteType = 'arch'
      this.deleteDialog = true
    },
    async seedArchs () {
      this.seedingArchs = true
      try {
        const { data } = await apiService.post('admin/llm_models/jetson-model-arch/seed-defaults')
        this.showSnack(data.message)
        this.fetchArchs()
      } catch (e) { this.showSnack('Seed failed', 'error') }
      this.seedingArchs = false
    },
    // --- Delete ---
    onDeleted ({ type, message }) {
      this.showSnack(message)
      if (type === 'device') this.fetchDevices()
      else this.fetchArchs()
    }
  }
}
</script>
